import { makeAutoObservable } from 'mobx';
import type { StoryData } from 'storyblok-js-client';
import createStoreContext from './createStoreContext';
import type { GlobalSettings } from '../util/storyblok/types/global.types';
import type { ArticleHeaderBlokData } from '../components/organisms/article-header/ArticleHeader.data';
import type { VideoModalData } from '../components/molecules/video-modal/VideoModal.data';
import type { LocationBlokData } from '../components/blocks/location/Location.data';
import type { FeaturedNewsItemBlokData } from '../components/blocks/featured-news-overview/featured-news-item/FeaturedNewsItem.data';
import type { CaseBlokData } from '../components/blocks/case/Case.data';

export const [GlobalStoreProvider, useGlobalStore] = createStoreContext(
  class GlobalStore {
    public globalSettings!: GlobalSettings;
    // public globalLinks!: GlobalLinks;
    public featuredNews!: Array<FeaturedNewsItemBlokData> | undefined;
    public recentNews!: Array<StoryData<ArticleHeaderBlokData>> | undefined;
    public locationsList: Array<LocationBlokData> | undefined;
    public nextStory!: StoryData | undefined | false;
    public toPath!: string | undefined;
    public fromPath!: string | undefined;
    public menuIsOpen: boolean = false;
    public localeListIsOpen: boolean = false;
    public showPageWipeEffect: boolean = false;
    public allCases: Array<StoryData<CaseBlokData>> | undefined;
    public countryCode: string = '';

    public videoModalData!: VideoModalData;
    public makeVideoModalVisible: boolean = false;
    public hostName?: string;
    private renderLocaleListCallback?: (isOpen: boolean) => void;

    public constructor() {
      // NOTE: don't add anything else here  unless you know what you are doing
      // as the constructor will be executed twice due to `StrictMode`
      makeAutoObservable(this);
    }

    // todo should move this to a VideoModalContext(createContext/useContext) instead orso.
    public openVideoModal(data: VideoModalData): void {
      this.makeVideoModalVisible = true;
      this.videoModalData = data;
    }

    public closeVideoModal(): void {
      this.makeVideoModalVisible = false;
      this.videoModalData = null;
    }

    public toggleMenuOpenState(): void {
      this.menuIsOpen = !this.menuIsOpen;
    }

    public coverPageWipeEffect(cover: boolean): void {
      this.showPageWipeEffect = cover;
    }

    public setBeforeHistoryChangePath(path: string, fromPath: string): void {
      this.fromPath = fromPath;
      this.toPath = path;
    }

    public setMenuOpenState(isOpen: boolean): void {
      this.menuIsOpen = isOpen;
    }

    public setRenderLocaleListCallback(callback: (isOpen: boolean) => void): void {
      this.renderLocaleListCallback = callback;
    }

    public setLocaleListOpenState(isOpen: boolean): void {
      if (this.renderLocaleListCallback) {
        this.renderLocaleListCallback(isOpen);
      }

      this.localeListIsOpen = isOpen;
    }

    public setGlobalSettings = (globalSettings: GlobalSettings) => {
      this.globalSettings = globalSettings;
    };

    // public setGlobalLinks = (links: GlobalLinks) => {
    //   this.globalLinks = links;
    // };

    public setNextStoryData(nextStory: StoryData | undefined | false): void {
      this.nextStory = nextStory;
    }

    public setFeaturedNews(newsItems: Array<FeaturedNewsItemBlokData>): void {
      this.featuredNews = newsItems;
    }

    public setHostName(hostName?: string): void {
      this.hostName = hostName;
    }

    public setRecentNewsData(
      recentNews: Array<StoryData<ArticleHeaderBlokData>> | undefined,
    ): void {
      this.recentNews = recentNews;
    }

    public setLocationsListData(locationsList: Array<LocationBlokData> | undefined) {
      this.locationsList = locationsList;
    }

    public setCountryCode(countryCode: string) {
      this.countryCode = countryCode;
    }

    public setAllCases(allCases: Array<StoryData<CaseBlokData>> | undefined = this.allCases): void {
      if (this.countryCode) {
        this.allCases = allCases?.filter((caseItem) => {
          if (caseItem.content.countries?.length > 0) {
            if (caseItem.content.exclude_countries) {
              return !caseItem.content.countries?.includes(this.countryCode);
            } // else
            return caseItem.content.countries?.includes(this.countryCode);
          }
          return true;
        });
      } else {
        this.allCases = allCases;
      }
    }
  },
);

export type GlobalStore = ReturnType<typeof useGlobalStore>;
